if (!customElements.get('dd-cart-page')) {
  customElements.define(
    'dd-cart-page',
    class DDCartPage extends HTMLElement {
      constructor() {
        super();
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        if (this.dataset.ddBound === 'true') return;
        this.dataset.ddBound = 'true';
        this.addEventListener('submit', this.handleSubmit);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.handleSubmit);
        this.removeEventListener('click', this.handleClick);
        delete this.dataset.ddBound;
      }

      handleSubmit(event) {
        const recommendationForm = event.target.closest('[data-dd-mini-cart-recommendation-form]');
        if (recommendationForm) {
          event.preventDefault();
          this.addRecommendation(recommendationForm);
          return;
        }

        const discountForm = event.target.closest('[data-dd-mini-cart-discount-form]');
        if (!discountForm) return;
        event.preventDefault();
        this.updateDiscount(discountForm);
      }

      handleClick(event) {
        const removeDiscountButton = event.target.closest('[data-dd-mini-cart-remove-discount]');
        if (!removeDiscountButton) return;
        event.preventDefault();
        this.updateDiscount(null, removeDiscountButton);
      }

      async addRecommendation(form) {
        const submitButton = form.querySelector('[type="submit"]');
        const variantId = Number(new FormData(form).get('id'));
        if (!variantId || submitButton?.disabled) return;

        const defaultContent = submitButton?.querySelector('[data-dd-recommendation-default]');
        const pendingContent = submitButton?.querySelector('[data-dd-recommendation-pending]');
        const originalLabel = submitButton?.getAttribute('aria-label');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.setAttribute('aria-busy', 'true');
          if (pendingContent) submitButton.setAttribute('aria-label', pendingContent.textContent.trim());
        }
        if (defaultContent) defaultContent.hidden = true;
        if (pendingContent) pendingContent.hidden = false;
        this.showPageError('');

        try {
          const state = await this.mutate(`${this.shopRoot()}cart/add.js`, {
            items: [{
              id: variantId,
              quantity: 1,
              properties: { _dd_add_placement: 'cart' },
            }],
          });
          window.DDTracking?.add(state.items?.map((item) => ({ ...item, addedQuantity: 1 })), form);
          await this.renderSections(state, {
            context: 'page',
            selector: `[data-quantity-variant-id="${CSS.escape(String(variantId))}"]`,
            useLineTitle: true,
          });
        } catch (error) {
          this.showPageError(error?.message || this.dataset.ddRecommendationError);
        } finally {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.removeAttribute('aria-busy');
            if (originalLabel !== null) submitButton.setAttribute('aria-label', originalLabel);
          }
          if (defaultContent) defaultContent.hidden = false;
          if (pendingContent) pendingContent.hidden = true;
        }
      }

      async updateDiscount(form, trigger) {
        const isRemoval = !form;
        const input = form?.querySelector('[name="discount"]');
        const submitButton = form?.querySelector('[type="submit"]');
        const errorElement = form?.querySelector('[data-dd-mini-cart-discount-error]');
        const code = isRemoval ? '' : input?.value.trim();

        if (!isRemoval && !code) {
          this.showDiscountError(errorElement, this.dataset.ddPromoInvalid);
          input?.focus();
          return;
        }
        if (submitButton?.disabled || trigger?.disabled) return;

        if (submitButton) submitButton.disabled = true;
        if (trigger) trigger.disabled = true;
        this.showDiscountError(errorElement, '');

        try {
          const state = await this.mutate(`${this.shopRoot()}cart/update.js`, { discount: code });
          const appliedCodes = (state.cart_level_discount_applications || [])
            .filter((discount) => discount.type === 'discount_code')
            .map((discount) => String(discount.title || discount.code || '').toLowerCase());
          const codeWasApplied = isRemoval || appliedCodes.includes(code.toLowerCase());

          await this.renderSections(state);
          if (!codeWasApplied) {
            const refreshedDetails = this.querySelector('.dd-cart-summary--page .dd-mini-cart__promo');
            if (refreshedDetails) refreshedDetails.open = true;
            const refreshedInput = this.querySelector('.dd-cart-summary--page [name="discount"]');
            const refreshedError = this.querySelector('.dd-cart-summary--page [data-dd-mini-cart-discount-error]');
            if (refreshedInput) refreshedInput.value = code;
            this.showDiscountError(refreshedError, this.dataset.ddPromoInvalid);
            this.focusAfterRender('.dd-cart-summary--page [name="discount"]');
            return;
          }

          this.focusAfterRender(
            isRemoval
              ? '.dd-cart-summary--page [name="discount"]'
              : '.dd-cart-summary--page [data-dd-mini-cart-remove-discount]'
          );
        } catch (_error) {
          if (submitButton) submitButton.disabled = false;
          if (trigger) trigger.disabled = false;
          this.showDiscountError(errorElement, this.dataset.ddPromoError);
          (input || trigger)?.focus();
        }
      }

      async mutate(url, payload) {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...payload,
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || !state.sections) throw new Error(state.description || state.message);
        return state;
      }

      sectionIds() {
        const cartDrawer = document.querySelector('cart-drawer');
        return cartDrawer?.sectionIds?.() || [this.dataset.sectionId, 'cart-icon-bubble', 'cart-drawer'].filter(Boolean);
      }

      async renderSections(state, focus) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer?.commitContents) throw new Error(this.dataset.ddRecommendationError);

        const committedState = await cartDrawer.commitContents(state, { shouldOpen: false, focus });

        await cartDrawer.publishCartUpdate?.(committedState, 'dd-cart-page');

        return committedState;
      }

      focusAfterRender(selector, useLineTitle = false) {
        document.querySelector('cart-drawer')?.focusAfterCartUpdate?.({
          context: 'page',
          selector,
          useLineTitle,
        });
      }

      showDiscountError(element, message) {
        if (!element) return;
        element.textContent = message || '';
        element.hidden = !message;
        const input = element.closest('form')?.querySelector('[name="discount"]');
        if (message) input?.setAttribute('aria-invalid', 'true');
        else input?.removeAttribute('aria-invalid');
      }

      showPageError(message) {
        const errorElement = this.querySelector('#cart-errors');
        if (errorElement) errorElement.textContent = message || '';
      }

      shopRoot() {
        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }
    }
  );
}
